import client from 'apollo/client';

import type {
  ClassroomIndependentPracticeSubjectsQuery,
  ClassroomIndependentPracticeSubjectsQueryVariables,
  IndependentPracticeSubjectsQuery,
  IndependentPracticeSubjectsQueryVariables
} from '../queries/IndependentPractice.queries.generated';
import {
  ClassroomIndependentPracticeSubjectsDocument,
  IndependentPracticeSubjectsDocument
} from '../queries/IndependentPractice.queries.generated';
import type {IndependentPracticeSubject} from '../types';
import {useStudentHomepageStore} from '../../useStudentHomepageStore';

/**
 * `subjects` is keyset-paginated with a server-side maximum of 250 per page, which stands in
 * for the legacy query's `pageSize('infinite')` — the practice catalog is an order of
 * magnitude smaller than that ceiling.
 */
const SUBJECTS_PAGE_SIZE = 250;

// Responses write to a module-global store slice, and the tab remounts on every
// classroom switch — drop out-of-order responses so a slow fetch from a previous
// scope can't overwrite the current one.
let latestRequestId = 0;

/**
 * Drops the cached subject lists so the next fetch hits the network.
 * Call on enrollment changes; plain tab remounts are served from the Apollo cache.
 */
export function invalidateIndependentPracticeSubjectsCache() {
  // Both operations read the same root `subjects` field, so evicting the field drops every
  // cached scope at once — the scope-less list and each classroom-scoped one.
  client.cache.evict({id: 'ROOT_QUERY', fieldName: 'subjects'});
  client.cache.gc();
}

export default function useIndependentPracticeSubjects() {
  const areIndependentPracticeSubjectsErrored = useStudentHomepageStore(
    (state) => state.areIndependentPracticeSubjectsErrored
  );
  const areIndependentPracticeSubjectsLoading = useStudentHomepageStore(
    (state) => state.areIndependentPracticeSubjectsLoading
  );
  const independentPracticeSubjects = useStudentHomepageStore(
    (state) => state.independentPracticeSubjects
  );
  const setAreIndependentPracticeSubjectsErrored = useStudentHomepageStore(
    (state) => state.setAreIndependentPracticeSubjectsErrored
  );
  const setAreIndependentPracticeSubjectsLoading = useStudentHomepageStore(
    (state) => state.setAreIndependentPracticeSubjectsLoading
  );
  const setIndependentPracticeSubjects = useStudentHomepageStore(
    (state) => state.setIndependentPracticeSubjects
  );

  function fetchIndependentPracticeSubjects(classroomId: string | undefined) {
    const requestId = ++latestRequestId;

    setAreIndependentPracticeSubjectsLoading(true);
    setAreIndependentPracticeSubjectsErrored(false);

    const request = classroomId
      ? client.query<
          ClassroomIndependentPracticeSubjectsQuery,
          ClassroomIndependentPracticeSubjectsQueryVariables
        >({
          query: ClassroomIndependentPracticeSubjectsDocument,
          variables: {classroomId, first: SUBJECTS_PAGE_SIZE}
        })
      : client.query<IndependentPracticeSubjectsQuery, IndependentPracticeSubjectsQueryVariables>({
          query: IndependentPracticeSubjectsDocument,
          variables: {first: SUBJECTS_PAGE_SIZE}
        });

    request
      .then(({data}) => {
        if (requestId !== latestRequestId) return;

        const subjects: IndependentPracticeSubject[] = data?.subjects?.results ?? [];
        setIndependentPracticeSubjects(subjects);
      })
      .catch(() => {
        // A rejected query writes nothing to the Apollo cache, so the next fetch retries on
        // its own — unlike the legacy Mandark query, whose interest had to be re-dirtied here
        // or a single failure was served as an empty success on every later visit.
        if (requestId !== latestRequestId) return;

        // The tab renders an inline error/retry state off this flag, so no toast here.
        setAreIndependentPracticeSubjectsErrored(true);
        setIndependentPracticeSubjects([]);
      })
      .finally(() => {
        if (requestId !== latestRequestId) return;

        setAreIndependentPracticeSubjectsLoading(false);
      });
  }

  return {
    areIndependentPracticeSubjectsErrored,
    areIndependentPracticeSubjectsLoading,
    fetchIndependentPracticeSubjects,
    independentPracticeSubjects
  };
}
